import { makeZip } from 'client-zip';
import { downloadApi } from '../../api/download';
import { fetchInOrder } from './fetchPipeline';
import { entryName, isMobileLike, planParts, targetBytesFor, MOBILE_REFUSE_BYTES } from './partPlanner';
import { createSink, type DownloadSink } from './sinks';
import { UrlProvider } from './urlProvider';
import type {
  DownloadManifest,
  DownloadPart,
  DownloadProgress,
  DownloadScope,
  DownloadTarget,
  ManifestFile,
  PendingSave,
  SkippedFile,
} from './types';

/**
 * Orchestrates a client-side ZIP download end to end: manifest → part plan →
 * sink → ordered fetch → archive → disk.
 *
 * Nothing is built on the server. That means the archive always reflects the
 * album as it is right now, there is no size cap, and progress is real rather
 * than a spinner over an opaque Lambda.
 */

const CONCURRENCY_DESKTOP = 5;
const CONCURRENCY_MOBILE = 3;
const BUFFER_DESKTOP = 96 * 1024 ** 2;
const BUFFER_MOBILE = 32 * 1024 ** 2;

/** Progress is throttled to this cadence — a React update per chunk janks the page. */
const EMIT_INTERVAL_MS = 250;
/** Sliding window for speed/ETA. A cumulative average lies for the first minute. */
const RATE_WINDOW_MS = 10_000;
/** Re-estimate the total after this many files when sizes were estimates. */
const REESTIMATE_EVERY = 20;
/** Blob sink: pause after this many unsaved parts pile up in memory. */
const MAX_UNSAVED_PARTS = 2;

export interface StartOptions {
  /** Album or event this download is scoped to. */
  target: DownloadTarget;
  albumName: string;
  scope: DownloadScope;
  photoIds?: string[];
  onProgress: (p: DownloadProgress) => void;
  /** Ask the user to confirm an oversized mobile download. Return false to abort. */
  confirmLarge?: (bytes: number, partCount: number) => Promise<boolean>;
}

export class ZipDownload {
  private controller = new AbortController();
  private sink: DownloadSink | null = null;
  private provider: UrlProvider | null = null;
  private manifest: DownloadManifest | null = null;
  private parts: DownloadPart[] = [];

  private bytesDone = 0;
  private filesDone = 0;
  private partIndex = 0;
  private currentFile: string | null = null;
  private skipped: SkippedFile[] = [];
  private pendingSaves: PendingSave[] = [];
  private phase: DownloadProgress['phase'] = 'planning';
  private error: string | null = null;

  /** Running total, corrected as real sizes land (see reestimate). */
  private bytesTotal = 0;
  private estimated = false;
  private actualSoFar = 0;
  private declaredSoFar = 0;

  private samples: Array<{ t: number; bytes: number }> = [];
  private lastEmit = 0;
  private startedAt = Date.now();
  private wakeLock: { release: () => Promise<void> } | null = null;
  private saveResolvers = new Map<number, () => void>();
  /** partIndex → re-save the finished archive. Populated as parts complete. */
  private opens = new Map<number, () => Promise<void>>();
  /** partIndex → write this part to a user-chosen destination. */
  private saves = new Map<number, () => Promise<void>>();
  /** Captured at plan time — `sink.dispose()` clears the handle it comes from. */
  private savedTo: { kind: 'file' | 'folder'; name: string } | null = null;

  constructor(private readonly opts: StartOptions) {}

  // ── Public control surface ─────────────────────────────────────────────────

  get target(): DownloadTarget {
    return this.opts.target;
  }

  cancel(): void {
    this.controller.abort();
  }

  /**
   * Write a finished part to wherever the user chooses. Must be called from a
   * user gesture — on the OPFS sink this opens the save picker.
   */
  async savePart(partIndex: number): Promise<void> {
    const save = this.saves.get(partIndex);
    if (!save) return;
    try {
      await save();
    } catch {
      // Picker dismissed, or the write failed — leave the part pending so the
      // button stays available rather than silently losing the archive.
      return;
    }
    this.saves.delete(partIndex);
    this.pendingSaves = this.pendingSaves.filter((p) => p.partIndex !== partIndex);
    this.savedTo = this.sink?.destination ?? this.savedTo;
    this.saveResolvers.get(partIndex)?.();
    this.saveResolvers.delete(partIndex);
    this.emit(true);
  }

  /**
   * Hand a finished archive back to the browser. This is as close to "open" as
   * a web page gets — revealing a file in the OS file manager is not something
   * any browser exposes.
   */
  openPart(partIndex: number): void {
    void this.opens.get(partIndex)?.().catch(() => {});
  }

  /**
   * Save every finished part into one folder, with a single prompt. Falls back
   * to `false` when the browser has no directory picker or the user dismisses
   * it, in which case the per-part Save buttons still apply.
   */
  async saveAll(): Promise<boolean> {
    const sink = this.sink as { saveAllToDirectory?: () => Promise<boolean> } | null;
    if (!sink?.saveAllToDirectory) return false;
    const ok = await sink.saveAllToDirectory().catch(() => false);
    if (!ok) return false;
    this.saves.clear();
    this.pendingSaves = [];
    this.savedTo = this.sink?.destination ?? this.savedTo;
    for (const r of this.saveResolvers.values()) r();
    this.saveResolvers.clear();
    this.emit(true);
    return true;
  }

  /** True when a single-prompt "save all" is worth offering. */
  get canSaveAll(): boolean {
    const sink = this.sink as { saveAllToDirectory?: unknown } | null;
    return !!sink?.saveAllToDirectory && this.pendingSaves.length > 1;
  }

  /** Build a follow-up archive containing only the photos that failed. */
  async retrySkipped(): Promise<void> {
    const ids = this.skipped.map((s) => s.photoId);
    if (ids.length === 0) return;
    const retry = new ZipDownload({
      ...this.opts,
      scope: 'selection',
      photoIds: ids,
    });
    await retry.run();
  }

  // ── Main flow ──────────────────────────────────────────────────────────────

  async run(): Promise<void> {
    try {
      await this.plan();
      await this.download();
      this.phase = this.skipped.length > 0 && this.filesDone === 0 ? 'failed' : 'done';
      this.emit(true);
      this.report(false);
    } catch (err) {
      if (this.controller.signal.aborted) {
        this.phase = 'cancelled';
        this.error = null;
        this.report(true);
      } else {
        this.phase = 'failed';
        this.error = err instanceof Error ? err.message : String(err);
        this.report(false);
      }
      this.emit(true);
    } finally {
      this.releaseWakeLock();
      this.sink?.dispose();
      // A cancelled or failed run leaves nothing worth saving — drop any staged
      // bytes now rather than letting them sit in OPFS.
      if (this.phase !== 'done') await this.discardStaged();
    }
  }

  private async plan(): Promise<void> {
    this.phase = 'planning';
    this.emit(true);

    const manifest = await downloadApi.getManifest(
      this.opts.target,
      this.opts.scope,
      this.opts.photoIds,
    );
    this.manifest = manifest;
    this.bytesTotal = manifest.totalBytes;
    this.estimated = manifest.estimated;

    // Choose where the bytes will wait. No picker opens here — every sink
    // defers the destination prompt until the archive is finished, so progress
    // starts immediately instead of behind a file dialog.
    const mobile = isMobileLike();
    this.sink = await createSink({ totalBytes: manifest.totalBytes });

    this.parts = planParts(manifest.files, {
      folderName: manifest.folderName,
      targetBytes: targetBytesFor(this.sink.kind, mobile),
    });

    if (this.sink.kind === 'blob' && manifest.totalBytes > MOBILE_REFUSE_BYTES && mobile) {
      throw new Error(
        'This download is too large for a phone. Please use a computer for downloads over 5 GB.',
      );
    }
    if (this.opts.confirmLarge && this.parts.length > 1) {
      const ok = await this.opts.confirmLarge(manifest.totalBytes, this.parts.length);
      if (!ok) {
        this.controller.abort();
        throw new DOMException('Aborted', 'AbortError');
      }
    }

    this.provider = new UrlProvider(this.opts.target, manifest.sessionToken, manifest.files);
    await this.acquireWakeLock();
  }

  private async download(): Promise<void> {
    if (!this.sink || !this.provider || !this.manifest) throw new Error('Not planned');
    this.phase = 'downloading';
    this.startedAt = Date.now();

    for (const part of this.parts) {
      if (this.controller.signal.aborted) throw new DOMException('Aborted', 'AbortError');
      this.partIndex = part.index;
      await this.writePart(part);
    }
  }

  /** Stream one part's photos through client-zip into the sink. */
  private async writePart(part: DownloadPart): Promise<void> {
    const sink = this.sink!;
    const provider = this.provider!;
    const manifest = this.manifest!;
    const mobile = isMobileLike();

    const writer = await sink.openPart(part.name);

    // client-zip pulls entries from this generator, so fetching, archiving and
    // writing all happen in one pass — there is no separate "compressing" stage
    // and progress never has to lie about what it's doing.
    const entries = async function* (this: ZipDownload) {
      const pipeline = fetchInOrder(part.files, {
        getUrl: (file, force) => provider.getUrl(file, force),
        concurrency: mobile ? CONCURRENCY_MOBILE : CONCURRENCY_DESKTOP,
        maxBufferedBytes: mobile ? BUFFER_MOBILE : BUFFER_DESKTOP,
        signal: this.controller.signal,
        onSkip: (s) => {
          this.skipped.push(s);
          this.emit();
        },
      });

      for await (const { file, data } of pipeline) {
        this.currentFile = file.name;
        this.filesDone++;
        this.trackActual(file, data.byteLength);
        provider.release(file.photoId);
        yield { name: entryName(manifest.folderName, file), input: data, lastModified: new Date() };
      }
    }.bind(this);

    const stream = makeZip(entries());
    const reader = stream.getReader();

    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        if (this.controller.signal.aborted) throw new DOMException('Aborted', 'AbortError');
        await writer.write(value);
        this.bytesDone += value.byteLength;
        this.sample(value.byteLength);
        this.emit();
      }
    } catch (err) {
      await reader.cancel().catch(() => {});
      await writer.abort().catch(() => {});
      throw err;
    }

    const completed = await writer.close();

    // Remember how to re-open this archive (where the platform allows it) and,
    // more importantly, how to save it — that only happens once the user asks.
    if (completed.open) this.opens.set(part.index, completed.open);

    if (completed.save) {
      this.saves.set(part.index, completed.save);
      this.pendingSaves.push({
        partIndex: part.index,
        name: part.name,
        inMemory: completed.inMemory,
      });
      // Only memory-held parts create pressure to save promptly; staged ones
      // sit on disk and can wait for the user indefinitely.
      if (completed.inMemory) await this.waitForSaveCapacity();
    }
    this.emit(true);
  }

  /**
   * Blob sink backpressure: unsaved parts stay in memory, so pause once a
   * couple have piled up rather than accumulating gigabytes the user is
   * ignoring.
   */
  private async waitForSaveCapacity(): Promise<void> {
    if (this.pendingSaves.length < MAX_UNSAVED_PARTS) return;
    const oldest = this.pendingSaves[0];
    this.phase = 'awaiting-save';
    this.emit(true);
    await new Promise<void>((resolve) => {
      this.saveResolvers.set(oldest.partIndex, resolve);
      this.controller.signal.addEventListener('abort', () => resolve(), { once: true });
    });
    this.phase = 'downloading';
  }

  // ── Size estimation ────────────────────────────────────────────────────────

  /**
   * Overlay albums composited before `processedBytes` existed report the
   * original's size, which is wrong for the file we actually download. Correct
   * the remaining total by the observed ratio so the bar doesn't drift.
   */
  private trackActual(file: ManifestFile, actualBytes: number): void {
    if (!this.estimated) return;
    this.actualSoFar += actualBytes;
    this.declaredSoFar += file.bytes;
    const n = ++this.reestimateCounter;
    if (n % REESTIMATE_EVERY === 0 && this.declaredSoFar > 0) {
      const ratio = this.actualSoFar / this.declaredSoFar;
      const remainingDeclared = (this.manifest?.totalBytes ?? 0) - this.declaredSoFar;
      this.bytesTotal = Math.round(this.actualSoFar + remainingDeclared * ratio);
    }
  }
  private reestimateCounter = 0;

  // ── Progress reporting ─────────────────────────────────────────────────────

  private sample(bytes: number): void {
    const now = Date.now();
    this.samples.push({ t: now, bytes });
    const cutoff = now - RATE_WINDOW_MS;
    while (this.samples.length > 0 && this.samples[0].t < cutoff) this.samples.shift();
  }

  private get bytesPerSec(): number {
    if (this.samples.length < 2) return 0;
    const span = (this.samples[this.samples.length - 1].t - this.samples[0].t) / 1000;
    if (span <= 0) return 0;
    const total = this.samples.reduce((sum, s) => sum + s.bytes, 0);
    return total / span;
  }

  private emit(force = false): void {
    const now = Date.now();
    if (!force && now - this.lastEmit < EMIT_INTERVAL_MS) return;
    this.lastEmit = now;

    const rate = this.bytesPerSec;
    const remaining = Math.max(0, this.bytesTotal - this.bytesDone);
    this.opts.onProgress({
      phase: this.phase,
      albumName: this.opts.albumName,
      bytesDone: this.bytesDone,
      bytesTotal: this.bytesTotal,
      filesDone: this.filesDone,
      filesTotal: this.manifest?.files.length ?? 0,
      partIndex: this.partIndex,
      partCount: this.parts.length,
      currentFile: this.currentFile,
      bytesPerSec: rate,
      etaSeconds: rate > 0 ? Math.round(remaining / rate) : null,
      skipped: [...this.skipped],
      pendingSaves: [...this.pendingSaves],
      openable: [...this.opens.keys()]
        .sort((a, b) => a - b)
        .map((partIndex) => ({ partIndex, name: this.parts[partIndex]?.name ?? '' })),
      savedTo: this.savedTo,
      estimated: this.estimated,
      sink: this.sink?.kind ?? null,
      error: this.error,
    });
  }

  private report(aborted: boolean): void {
    if (!this.manifest) return;
    // Reporting exists to feed the album's corrupt-photo list; the event
    // gallery has no such surface and no endpoint, so don't fire into a 404.
    if (this.opts.target.kind !== 'album') return;
    const body = {
      completed: this.filesDone,
      bytes: this.bytesDone,
      durationMs: Date.now() - this.startedAt,
      skipped: this.skipped,
    };
    if (aborted) {
      downloadApi.reportBeacon(this.opts.target, this.manifest.sessionToken, body);
    } else {
      void downloadApi.report(this.opts.target, this.manifest.sessionToken, { ...body, aborted });
    }
  }

  /**
   * Delete anything staged but never saved. Called when the download ends
   * badly, and by the store when the user dismisses the finished card.
   */
  async discardStaged(): Promise<void> {
    this.saves.clear();
    this.pendingSaves = [];
    const sink = this.sink as { cleanup?: () => Promise<void> } | null;
    await sink?.cleanup?.().catch(() => {});
  }

  // ── Screen wake lock ───────────────────────────────────────────────────────

  private async acquireWakeLock(): Promise<void> {
    try {
      const wl = (navigator as unknown as {
        wakeLock?: { request: (t: string) => Promise<{ release: () => Promise<void> }> };
      }).wakeLock;
      if (wl) this.wakeLock = await wl.request('screen');
    } catch {
      // Not supported, or denied in the background — harmless.
    }
  }

  private releaseWakeLock(): void {
    void this.wakeLock?.release().catch(() => {});
    this.wakeLock = null;
  }
}
